import { PROGRAMS } from "../data/programs";
import { PROGRAM_PROFILES } from "../data/programProfiles";
import { PROGRAM_NAME_MAP } from "../data/programMappings";

export function calculateScoreRanges(
  questions = []
) {
  const dimensions = [
    "inclusion",
    "control",
    "affection",
    "thinking",
    "flexibility"
  ];

  const ranges = {};

  dimensions.forEach(dimension => {
    ranges[dimension] = {
      min: 0,
      max: 0
    };
  });

  questions.forEach(question => {
    const options =
      question?.options || [];

    dimensions.forEach(dimension => {
      const possibleWeights =
        options.map(
          option =>
            option?.weights?.[dimension] ?? 0
        );

      ranges[dimension].min +=
        Math.min(...possibleWeights);

      ranges[dimension].max +=
        Math.max(...possibleWeights);
    });
  });

  return ranges;
}

/**
 * STEP 1: Build scores from weight-based answers
 */
export function buildScoresFromAnswers(answers) {
  const scores = {
    inclusion: 0,
    control: 0,
    affection: 0,
    thinking: 0,
    flexibility: 0
  };

  answers.forEach((answer) => {
    if (!answer?.weights) return;

    Object.entries(answer.weights).forEach(([dimension, value]) => {
      if (scores[dimension] !== undefined) {
        scores[dimension] += value;
      }
    });
  });

  return scores;
}

/**
 * STEP 2: Normalize scores (important for consistency)
 */
export function normalizeScores(
  scores,
  scoreRanges
) {
  const normalized = {};

  Object.entries(scores || {}).forEach(
    ([dimension, value]) => {
      const range =
        scoreRanges?.[dimension];

      if (
        typeof range?.min === "number" &&
        typeof range?.max === "number" &&
        range.max > range.min
      ) {
        const clamped = Math.max(
          range.min,
          Math.min(value, range.max)
        );

        normalized[dimension] =
          Math.round(
            (
              (clamped - range.min) /
              (range.max - range.min)
            ) * 100
          );

        return;
      }

      // Backward compatibility for older results
      const clamped = Math.max(
        -10,
        Math.min(value, 10)
      );

      normalized[dimension] =
        Math.round(
          ((clamped + 10) / 20) * 100
        );
    }
  );

  return normalized;
}

/**
 * STEP 4: Match scoring
 */

const FIT_WEIGHTS = {
  behaviour: 33.33,
  fitDimensions: 33.33,
  environment: 33.34
};

/**
 * STEP 5: Fit level
 */
function getFitLevel(percentage) {
  if (percentage >= 80) return "High";
  if (percentage >= 55) return "Moderate";
  return "Low";
}

function calculateSimilarity(applicantValue, targetValue) {

  const difference = Math.abs(
    applicantValue - targetValue
  );

  // Penalise larger differences more aggressively.
  const similarity =
    100 - (difference * 1.5);

  return Math.max(0, similarity);

}

function scoreBehaviour(applicant, behaviourProfile) {

  if (!behaviourProfile) return 0;

  let totalSimilarity = 0;
  let comparisons = 0;

  Object.entries(behaviourProfile).forEach(([dimension, target]) => {

    // Ignore non-numeric fields like "traits"
    if (typeof target !== "number") return;

    if (typeof applicant[dimension] !== "number") return;

    const similarity = calculateSimilarity(
      applicant[dimension],
      target
    );

    totalSimilarity += similarity;
    comparisons++;

  });

  if (comparisons === 0) return 0;

  const averageSimilarity =
    totalSimilarity / comparisons;

  /// Behaviour contributes its configured weight
  return (
    (averageSimilarity / 100) *
    FIT_WEIGHTS.behaviour
  );

}

function calculateFitDimensionScore(scores, programProfile) {

  if (!programProfile?.fitDimensions) {
    return 0;
  }

  const applicant = {
    analytical:
      (scores.thinking + scores.control) / 2,

    social:
      (scores.affection + scores.inclusion) / 2,

    structure:
      (scores.control + scores.thinking) / 2,

    creativity:
      (scores.thinking + scores.flexibility) / 2,

    emotionalDemand:
      scores.affection,

    adaptability:
      scores.flexibility
  };

  let totalSimilarity = 0;
let count = 0;

Object.entries(programProfile.fitDimensions).forEach(
  ([dimension, target]) => {

    if (
      typeof applicant[dimension] === "number" &&
      typeof target === "number"
    ) {

      totalSimilarity += calculateSimilarity(
        applicant[dimension],
        target
      );

      count++;

    }

  }
);

if (count === 0) return 0;

const averageSimilarity =
  totalSimilarity / count;

return (
  (averageSimilarity / 100) *
  FIT_WEIGHTS.fitDimensions
);

}

function buildApplicantProfile(scores) {

  return {

    // Behavioural Dimensions
    inclusion: scores.inclusion,
    control: scores.control,
    affection: scores.affection,
    thinking: scores.thinking,
    flexibility: scores.flexibility,

    // Derived Fit Dimensions
    analytical:
      (scores.thinking + scores.control) / 2,

    social:
      (scores.inclusion + scores.affection) / 2,

    structure:
      (scores.control + scores.thinking) / 2,

    creativity:
      (scores.flexibility + scores.thinking) / 2,

    emotionalDemand:
      scores.affection,

    adaptability:
      scores.flexibility

  };

}

/**
 * STEP 6: Calculate fit per program
 */
export function calculateFit(rawScores, program) {
  if (!program?.profile) {
    return { percentage: 0, level: "Low" };
  }

  const scores = normalizeScores(rawScores);

  const profileKey =
    PROGRAM_NAME_MAP[program.name] || program.name;

  const programProfile =
    PROGRAM_PROFILES[profileKey];

  const applicant = buildApplicantProfile(scores);

  const behaviourPoints = scoreBehaviour(
    applicant,
    program.profile
  );

  // -----------------------------
  // FIT DIMENSIONS
  // -----------------------------

  const fitDimensionScore = calculateFitDimensionScore(
    scores,
    programProfile
  );

  let environmentScore = 0;

  if (programProfile?.environmentTraits) {

    let totalSimilarity = 0;
    let comparisons = 0;

    Object.entries(programProfile.environmentTraits).forEach(
      ([dimension, target]) => {

        if (
          typeof applicant[dimension] !== "number" ||
          typeof target !== "number"
        ) {
          return;
        }

      totalSimilarity += calculateSimilarity(
        applicant[dimension],
        target
      );

      comparisons++;
    }
  );

  if (comparisons > 0) {

    environmentScore =
      (totalSimilarity / comparisons / 100) *
      FIT_WEIGHTS.environment;

  }

}

  const finalScore =
    behaviourPoints +
    fitDimensionScore +
    environmentScore;

  // -----------------------------
  // FINAL SCORE
  // -----------------------------

  const percentage = Math.round(finalScore);

  const level = getFitLevel(percentage);

  return {
    percentage,
    level,
    applicant,
    programProfile
  };
}

/**
 * STEP 7: Generate all program fits
 */
export function generateFitsFromScores(scores, programs) {
  const source = programs || PROGRAMS;

  return source.map((program) => {
    const {
        percentage,
        level,
        applicant,
        programProfile
    } = calculateFit(scores, program);

    return {
      program: program.name,
      percentage,
      level,
      applicant,
      programProfile
    };
  });
}

